import {
  createContext,
  useContext,
  useEffect,
  type ReactNode,
} from 'react';

import type { AssetLibraryEmbedConfig } from './assetLibraryEmbedConfig';
import { AssetLibraryMediaSelectionProvider } from './hooks/useAssetLibraryMediaSelection';
import { useStore } from './assetLibraryStore';

type AssetLibraryShellContextValue = {
  embedConfig: AssetLibraryEmbedConfig | null;
  /** Compact layout for embedded modal surfaces (independent of embed config). */
  compact: boolean;
};

const AssetLibraryShellContext = createContext<AssetLibraryShellContextValue>({
  embedConfig: null,
  compact: false,
});

export function useAssetLibraryEmbedConfig(): AssetLibraryEmbedConfig | null {
  return useContext(AssetLibraryShellContext).embedConfig;
}

export function useAssetLibraryCompactLayout(): boolean {
  return useContext(AssetLibraryShellContext).compact;
}

export function AssetLibraryEmbedProvider({
  config,
  compact = false,
  children,
}: {
  config: AssetLibraryEmbedConfig | null;
  compact?: boolean;
  children: ReactNode;
}) {
  return (
    <AssetLibraryShellContext.Provider value={{ embedConfig: config, compact }}>
      <AssetLibraryEmbedConfigurator config={config} />
      <AssetLibraryMediaSelectionProvider embedConfig={config}>
        {children}
      </AssetLibraryMediaSelectionProvider>
    </AssetLibraryShellContext.Provider>
  );
}

function AssetLibraryEmbedConfigurator({
  config,
}: {
  config: AssetLibraryEmbedConfig | null;
}) {
  const store = useStore();

  useEffect(() => {
    store.setEmbedConfig(config);
    if (config) {
      void store.initializeForEmbed();
    }

    return () => {
      store.setEmbedConfig(null);
    };
  }, [config, store]);

  return null;
}
